import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeadCashes from "./headCashes";
import DetailCashAdd from "./detailCashAdd";
import { Box, Button } from "@mui/material";
import { FormActions } from "../../components/ui/form";
import { ErrorModal } from "../../components/ModalAlert";
import supabase from "../../supabase";
import { LoaderSpinner } from "../../components/spinner/loader";
import { formatDateForDb } from "../../utils/dateUtils";
import { syncChequesCashBalance } from "../../utils/chequesCash";

const empty = (value) => value === undefined || value === null || value === '';

const CashAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location?.state;
  const [loading, setLoading] = useState(false);
  const [usedBalance, setUsedBalance] = useState(0);
  const [description, setDescription] = useState('');
  const [amountToAdd, setAmountToAdd] = useState('');
  const [cashIdSelected, setCashIdSelected] = useState('');
  const [cashId] = useState(1);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [checks, setChecks] = useState([]);
  const [cashes, setCashes] = useState([]);
  const [balance, setBalance] = useState('');
  const [cashesAdd, setCashesAdd] = useState([]);
  const [selectedChecksNumbers, setSelectedChecksNumbers] = useState('');

  const handleAmountToAddChange = (newAmountToAdd) => setAmountToAdd(newAmountToAdd);
  const handleCashIdSelectedChange = (newCashIdSelected) => setCashIdSelected(newCashIdSelected);
  const handleDescriptionChange = (newDescription) => setDescription(newDescription);
  const updateCashesAdd = (newDetails) => setCashesAdd(newDetails);

  const allInputsValid = () => {
    if (!cashIdSelected) return false;
    if (cashIdSelected === '7') {
      if (checks.length === 0) return false;
      const allChecksValid = checks.every((check) => {
        if (empty(check.amount) || parseFloat(check.amount) <= 0) return false;
        if (empty(check.number) || empty(check.bank) || empty(check.branch)) return false;
        if (empty(check.issueDate) || empty(check.paymentDate)) return false;
        if (empty(check.signer) || empty(check.cuit)) return false;
        if (new Date(check.issueDate) > new Date(check.paymentDate)) return false;
        return true;
      });
      return allChecksValid;
    }
    return amountToAdd > 0 && description !== '';
  };

  const saveOperation = async () => {
    setLoading(true);
    if (allInputsValid()) {
      try {
        if (cashIdSelected === '7') {
          // Flujo para ingreso de cheques: crear payment, paymentDetails, cashMovements
          const totalAmount = checks.reduce((acc, c) => acc + parseFloat(c.amount || 0), 0);
          const checksNumbers = checks.map((c) => c.number).filter(Boolean).join(' - ');

          const { data: paymentData, error: paymentError } = await supabase
            .from("payments")
            .insert([{
              createdAt: new Date(),
              totalAmount: totalAmount,
              description: description || `Ingreso de cheque [ ${checksNumbers} ]`
            }])
            .select("paymentId");

          if (paymentError) {
            ErrorModal({ title: "Error", text: paymentError.message });
            setLoading(false);
            return;
          }

          const paymentId = paymentData[0].paymentId;

          const paymentDetailsToInsert = checks.map((check) => ({
            paymentId,
            paymentMethod: 7,
            amount: parseFloat(check.amount),
            checkNumber: check.number,
            checkBank: check.bank,
            checkAmount: parseFloat(check.amount),
            checkEmitionDate: formatDateForDb(check.issueDate),
            checkPaymentDate: formatDateForDb(check.paymentDate),
            checkIssuerName: check.signer,
            checkIssuerCuit: check.cuit,
            checkState: 0,
            checkNotes: check.notes
          }));

          const { error: detailsError } = await supabase
            .from("paymentDetails")
            .insert(paymentDetailsToInsert);

          if (detailsError) {
            ErrorModal({ title: "Error", text: detailsError.message });
            setLoading(false);
            return;
          }

          const cashMovementsToInsert = {
            amount: totalAmount,
            description: checksNumbers ? `${description || 'Ingreso de cheque'} [ ${checksNumbers} ]` : (description || 'Ingreso de cheque'),
            paymentMethod: cashIdSelected,
            createdAt: new Date(),
            cashId: cashIdSelected
          };

          await supabase.from("cashMovements").insert(cashMovementsToInsert);

          await syncChequesCashBalance();
        } else {
          // Flujo normal para otras cajas
          const cashMovementsToInsert = {
            amount: amountToAdd,
            description: selectedChecksNumbers ? `${description} [ ${selectedChecksNumbers} ]` : description,
            paymentMethod: cashIdSelected,
            createdAt: new Date(),
            cashId: cashIdSelected
          };

          await supabase.from("cashMovements").insert(cashMovementsToInsert);

          const { data: cashData } = await supabase
            .from("cash")
            .select("balance")
            .eq("cashId", cashMovementsToInsert.cashId)
            .single();

          const updatedBalance = parseFloat(cashData.balance) + parseFloat(cashMovementsToInsert.amount);
          await supabase
            .from("cash")
            .update({ balance: updatedBalance })
            .eq("cashId", cashMovementsToInsert.cashId);
        }
      } catch (error) {
        ErrorModal({ title: "Error", text: error.message });
      } finally {
        setLoading(false);
      }
      window.location.reload();
    } else {
      const errorMsg = cashIdSelected === '7'
        ? "Complete todos los datos de los cheques (número, banco, sucursal, fechas, firmante, CUIT y monto)."
        : "Por favor, complete todos los campos obligatorios.";
      ErrorModal({ title: "Error", text: errorMsg });
      setLoading(false);
    }
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
      }}
    >
      <HeadCashes />
      <DetailCashAdd
        onCashIdSelectedChange={handleCashIdSelectedChange}
        onAmountToAddChange={handleAmountToAddChange}
        onDescriptionChange={handleDescriptionChange}
        updateCashesAdd={updateCashesAdd}
        allInputsValid={allInputsValid}
        setSelectedChecksNumbers={setSelectedChecksNumbers}
        setChecks={setChecks}
        cashIdSelected={cashIdSelected}
      />
      <FormActions>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ minHeight: 44 }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveOperation}
          sx={{ minHeight: 44, flexGrow: { xs: 1, md: 0 }, minWidth: 200 }}
        >
          Guardar ingreso
        </Button>
      </FormActions>
    </Box>
  );
};

export default CashAdd;
